/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { ActionButton, View, Text } from '@components/atoms'
import { Modal } from '@components/molecules'
import { GridToolBar } from './GridToolBar'
import { GridSidePanel, SidePanelTab } from './GridSidePanel'
import { CircularProgress } from '@mui/material'
import {
	DataGrid,
	DataGridProps,
	GridValidRowModel,
	GridColDef,
	GridRowsProp,
	GridRowModel,
	GridRenderCellParams,
	useGridApiRef,
	GridPagination,
	useGridApiContext,
	useGridSelector,
	gridPageCountSelector,
	GridRenderEditCellParams,
	GridEditInputCell,
	GridEditBooleanCell,
	GridRowParams,
	GridRowSelectionModel,
	GridFilterOperator,
	GridPaginationModel,
} from '@mui/x-data-grid'
import { koKR } from '@mui/x-data-grid/locales'
import MuiPagination from '@mui/material/Pagination'
import { TablePaginationProps } from '@mui/material/TablePagination'
import { GridColumnType } from '@grid'
// `@mui/x-data-grid-generator`의 randomId 단순 대체 — 의존성 제거 목적
const randomId = (): string => Math.random().toString(36).substring(2, 11)

// 모든 Grid 인스턴스가 공유하는 페이지 사이즈 영속화 키
const PAGE_SIZE_STORAGE_KEY = 'judahs.grid.pageSize'
const PAGE_SIZE_OPTIONS = [25, 50, 100]
const DEFAULT_PAGE_SIZE = 25

const readSavedPageSize = (): number => {
	try {
		const raw = window.localStorage.getItem(PAGE_SIZE_STORAGE_KEY)
		const n = raw ? Number(raw) : DEFAULT_PAGE_SIZE
		return PAGE_SIZE_OPTIONS.includes(n) ? n : DEFAULT_PAGE_SIZE
	} catch {
		return DEFAULT_PAGE_SIZE
	}
}

// 날짜 범위 필터 — Community에선 같은 컬럼에 multi-item이 안 먹어 단일 item으로 처리
const DATE_BETWEEN_OPERATOR: GridFilterOperator = {
	label: 'between',
	value: 'between',
	getApplyFilterFn: filterItem => {
		if (!filterItem.value) return null
		const { from, to } = filterItem.value as { from?: string; to?: string }
		if (!from && !to) return null
		const fromDate = from ? new Date(from + 'T00:00:00') : null
		const toDate = to ? new Date(to + 'T23:59:59.999') : null
		return cellValue => {
			if (cellValue == null) return false
			const v = cellValue instanceof Date ? cellValue : new Date(cellValue)
			if (Number.isNaN(v.getTime())) return false
			if (fromDate && v < fromDate) return false
			if (toDate && v > toDate) return false
			return true
		}
	},
}
import { styled } from '@mui/material/styles'
import Tooltip, { tooltipClasses, TooltipProps } from '@mui/material/Tooltip'

import { grey, pink } from '@mui/material/colors'

interface PropsType extends Omit<DataGridProps, 'columns'> {
	rows: GridRowsProp
	columns: GridColumnType
	loading?: boolean
	onAction?: (iudFlag: string, aRow: GridRowModel) => void
	onAdd?: (aRow: GridRowModel) => void
	onUpdate?: (aRow: GridRowModel) => void
	onDelete?: (aRow: GridRowModel) => void
	renderCell?: {
		[key: string]: (params: GridRenderCellParams) => JSX.Element
	}
	renderEditCell?: {
		[key: string]: (params: GridRenderCellParams) => JSX.Element
	}
	isEditable?: boolean
	checkboxSelection?: boolean
	rowSelectionModel?: GridRowSelectionModel
	onRowSelectionModelChange?: (rowSelectionModel: GridRowSelectionModel) => void
	isRowSelectable?: (params: GridRowParams) => boolean
	sidePanel?: boolean
	title?: string
	onExcelUpload?: (rows: any[]) => void
	onExcelUploadClick?: () => void
	leftToolbarSlot?: React.ReactNode
}

function Pagination({
	page,
	onPageChange,
	className,
}: Pick<TablePaginationProps, 'page' | 'onPageChange' | 'className'>) {
	const apiRef = useGridApiContext()
	const pageCount = useGridSelector(apiRef, gridPageCountSelector)

	return (
		<MuiPagination
			color="primary"
			className={className}
			count={pageCount}
			page={page + 1}
			onChange={(event, newPage) => {
				onPageChange(event as any, newPage - 1)
			}}
		/>
	)
}

function CustomPagination(props: any) {
	return <GridPagination ActionsComponent={Pagination} {...props} />
}

declare module '@mui/x-data-grid' {
	interface ToolbarPropsOverrides {
		setRows: (newRows: (oldRows: GridRowsProp) => GridRowsProp) => void
		isLoading: boolean
		apiRef: React.MutableRefObject<any>
		onAddClick: () => void
		title?: string
		onExcelUpload?: (rows: any[]) => void
		onExcelUploadClick?: () => void
		leftToolbarSlot?: React.ReactNode
		editModeOn: boolean
		onEditModeToggle: (next: boolean) => void
		hasActionColumn: boolean
	}
}

export const Grid = (props: PropsType) => {
	// state
	const [rows, setRows] = React.useState(props.rows)
	const [showModal, setShowModal] = React.useState<boolean>(false)
	const [deleteRow, setDeleteRow] = React.useState<GridRowModel>({})
	const [editModeOn, setEditModeOn] = React.useState<boolean>(false)
	const [paginationModel, setPaginationModel] = React.useState<GridPaginationModel>(
		() => ({
			page: 0,
			pageSize: readSavedPageSize(),
		}),
	)
	const handlePaginationModelChange = React.useCallback((next: GridPaginationModel) => {
		setPaginationModel(next)
		try {
			window.localStorage.setItem(PAGE_SIZE_STORAGE_KEY, String(next.pageSize))
		} catch {
			/* noop */
		}
	}, [])

	// variables
	const isLoading: boolean = props.loading ?? false
	const isEditable: boolean = props.isEditable ?? true
	const checkboxSelection: boolean = props.checkboxSelection ?? false
	const sidePanelEnabled: boolean = props.sidePanel ?? true

	const [sidePanelTab, setSidePanelTab] = React.useState<SidePanelTab>(null)

	// 사용자 지정 컬럼 순서 (idx/actions 제외 user 컬럼들의 field 순서)
	const [columnOrder, setColumnOrder] = React.useState<string[]>(() =>
		props.columns.columns.map(c => c.field),
	)

	// props.columns 변경 시 새 컬럼 동기화 (기존 순서는 유지하되 사라진/새로 등장한 field 정리)
	React.useEffect(() => {
		const incoming = props.columns.columns.map(c => c.field)
		setColumnOrder(prev => {
			const kept = prev.filter(f => incoming.includes(f))
			const added = incoming.filter(f => !kept.includes(f))
			return [...kept, ...added]
		})
	}, [props.columns])

	const reorderColumn = React.useCallback((fromField: string, toField: string) => {
		setColumnOrder(prev => {
			const fromIdx = prev.indexOf(fromField)
			const toIdx = prev.indexOf(toField)
			if (fromIdx < 0 || toIdx < 0 || fromIdx === toIdx) return prev
			const next = [...prev]
			next.splice(fromIdx, 1)
			next.splice(toIdx, 0, fromField)
			return next
		})
	}, [])

	// 헤더 D&D — ag-grid 패턴: mousedown 후 5px 이상 이동해야 드래그 시작 (그 미만은 sort 클릭)
	const startHeaderDrag = React.useCallback(
		(downEvent: MouseEvent, fromField: string) => {
			if (downEvent.button !== 0) return
			const startX = downEvent.clientX
			const startY = downEvent.clientY
			let dragging = false
			let ghost: HTMLDivElement | null = null
			let lastTargetField: string | null = null

			const findHeaderField = (x: number, y: number): string | null => {
				const el = document.elementFromPoint(x, y) as HTMLElement | null
				const cell = el?.closest('.MuiDataGrid-columnHeader') as HTMLElement | null
				return cell?.getAttribute('data-field') ?? null
			}

			const onMove = (ev: MouseEvent) => {
				if (!dragging) {
					if (Math.abs(ev.clientX - startX) > 5 || Math.abs(ev.clientY - startY) > 5) {
						dragging = true
						document.body.style.userSelect = 'none'
						document.body.style.cursor = 'grabbing'
						ghost = document.createElement('div')
						const headerName =
							(apiRef.current as any)?.getColumn?.(fromField)?.headerName ?? fromField
						ghost.textContent = String(headerName)
						ghost.style.cssText =
							'position:fixed;pointer-events:none;z-index:9999;padding:4px 10px;background:#1976d2;color:#fff;border-radius:4px;font-size:12px;box-shadow:0 2px 6px rgba(0,0,0,0.25);'
						document.body.appendChild(ghost)
					}
				}
				if (dragging) {
					if (ghost) {
						ghost.style.left = `${ev.clientX + 12}px`
						ghost.style.top = `${ev.clientY + 8}px`
					}
					const f = findHeaderField(ev.clientX, ev.clientY)
					if (f !== lastTargetField) {
						// 이전 hover 표시 제거
						document
							.querySelectorAll('.MuiDataGrid-columnHeader.colDropHover')
							.forEach(n => n.classList.remove('colDropHover'))
						lastTargetField = f
						if (f) {
							const cell = document.querySelector(
								`.MuiDataGrid-columnHeader[data-field="${f}"]`,
							)
							cell?.classList.add('colDropHover')
						}
					}
				}
			}

			const cleanup = () => {
				document.removeEventListener('mousemove', onMove)
				document.removeEventListener('mouseup', onUp)
				document.body.style.userSelect = ''
				document.body.style.cursor = ''
				if (ghost) {
					ghost.remove()
					ghost = null
				}
				document
					.querySelectorAll('.MuiDataGrid-columnHeader.colDropHover')
					.forEach(n => n.classList.remove('colDropHover'))
			}

			const onUp = (ev: MouseEvent) => {
				if (dragging) {
					ev.preventDefault()
					ev.stopPropagation()
					const targetField = findHeaderField(ev.clientX, ev.clientY)
					if (
						targetField &&
						targetField !== fromField &&
						targetField !== 'idx' &&
						targetField !== 'actions' &&
						targetField !== '__check__'
					) {
						reorderColumn(fromField, targetField)
					}
				}
				cleanup()
			}

			document.addEventListener('mousemove', onMove)
			document.addEventListener('mouseup', onUp)
		},
		[reorderColumn],
	)

	const apiRef = useGridApiRef()

	// sort 변경 시 헤더 아이콘이 갱신되도록 sortModel을 자체 state로 구독
	const [sortModel, setSortModel] = React.useState<
		Array<{ field: string; sort: 'asc' | 'desc' | null | undefined }>
	>([])

	React.useEffect(() => {
		const api: any = apiRef.current
		if (!api?.subscribeEvent) return
		const unsub = api.subscribeEvent('sortModelChange', (model: any[]) => {
			setSortModel(model ?? [])
		})
		return () => {
			if (typeof unsub === 'function') unsub()
		}
	}, [apiRef])

	const toggleSort = React.useCallback(
		(field: string) => {
			const cur = sortModel.find(s => s.field === field)?.sort ?? null
			const next = cur === 'asc' ? 'desc' : cur === 'desc' ? null : 'asc'
			apiRef.current?.setSortModel(next ? [{ field, sort: next }] : [])
		},
		[apiRef, sortModel],
	)

	// 헤더 cell 전체에 mousedown delegation으로 D&D 시작 (sort 영역과 동일한 잡힘 영역)
	React.useEffect(() => {
		const root = (apiRef.current as any)?.rootElementRef?.current as
			| HTMLElement
			| undefined
		if (!root) return
		const container = root.querySelector<HTMLElement>('.MuiDataGrid-columnHeaders')
		if (!container) return

		const handler = (e: MouseEvent) => {
			const target = e.target as HTMLElement
			const cell = target.closest('.MuiDataGrid-columnHeader')
			if (!cell) return
			const field = cell.getAttribute('data-field')
			if (!field || field === 'idx' || field === 'actions' || field === '__check__')
				return
			// 메뉴 버튼/리사이즈 핸들/sort 버튼에서 시작한 경우 제외
			if (
				target.closest('.MuiDataGrid-menuIcon') ||
				target.closest('.MuiDataGrid-iconButtonContainer') ||
				target.closest('.MuiDataGrid-columnSeparator') ||
				target.closest('.gridSortBtn')
			)
				return
			startHeaderDrag(e, field)
		}

		container.addEventListener('mousedown', handler)
		return () => {
			container.removeEventListener('mousedown', handler)
		}
	}, [apiRef, startHeaderDrag, rows])

	// 사용자 스크롤 위치를 ref로 추적 — rows/loading 토글로 가상 스크롤러가
	// 잠시 사라졌다 다시 그려질 때도 마지막 위치로 복원하기 위함.
	const lastScrollRef = React.useRef<{ top: number; left: number }>({
		top: 0,
		left: 0,
	})

	const findScroller = React.useCallback((): HTMLElement | null => {
		const root = (apiRef.current as any)?.rootElementRef?.current as
			| HTMLElement
			| undefined
		return root?.querySelector<HTMLElement>('.MuiDataGrid-virtualScroller') ?? null
	}, [apiRef])

	// rows / loading 변경 시 가상 스크롤러가 reset되지 않도록 일정 시간 강제 복원
	React.useEffect(() => {
		setRows(props.rows)
		const target = lastScrollRef.current
		if (target.top === 0 && target.left === 0) return
		let cancelled = false
		let frames = 0
		const tick = () => {
			if (cancelled) return
			const s = findScroller()
			if (s && (s.scrollTop !== target.top || s.scrollLeft !== target.left)) {
				s.scrollTop = target.top
				s.scrollLeft = target.left
			}
			frames += 1
			if (frames < 30) requestAnimationFrame(tick)
		}
		requestAnimationFrame(tick)
		return () => {
			cancelled = true
		}
	}, [props.rows, props.loading, findScroller])

	// 가상 스크롤러에 scroll 리스너 — 사용자 마지막 위치를 ref에 누적
	React.useEffect(() => {
		const scroller = findScroller()
		if (!scroller) return
		const onScroll = () => {
			lastScrollRef.current = {
				top: scroller.scrollTop,
				left: scroller.scrollLeft,
			}
		}
		scroller.addEventListener('scroll', onScroll, { passive: true })
		return () => scroller.removeEventListener('scroll', onScroll)
	}, [findScroller, rows])

	// 수정 모드 토글에 따라 actions 컬럼 visibility 동기화
	React.useEffect(() => {
		if (apiRef.current?.getColumn?.('actions')) {
			apiRef.current.setColumnVisibility('actions', editModeOn)
		}
	}, [apiRef, editModeOn, rows])

	const StyledTooltip = styled(({ className, ...props }: TooltipProps) => (
		<Tooltip {...props} classes={{ popper: className }} />
	))(({ theme }) => ({
		[`& .${tooltipClasses.tooltip}`]: {
			backgroundColor: theme.palette.error.main,
			color: theme.palette.error.contrastText,
		},
	}))

	const EditInputCell = (props: GridRenderEditCellParams) => {
		const { error, errorMessage } = props

		const isError = error

		return (
			<StyledTooltip open={!!isError} title={errorMessage}>
				<GridEditInputCell
					{...props}
					css={isError ? { border: '1px solid', borderColor: pink['A400'] } : {}}
				/>
			</StyledTooltip>
		)
	}

	const renderEdit = (params: GridRenderEditCellParams) => {
		return <EditInputCell {...params} />
	}

	const rederCheckboxEdit = (params: GridRenderEditCellParams) => {
		return <GridEditBooleanCell {...params} />
	}

	const newColumns: GridColDef<GridValidRowModel>[] = React.useMemo(() => {
		const indexingCol: GridColDef = {
			field: 'idx',
			headerName: 'index',
			width: 80,
			headerAlign: 'center',
			align: 'center',
			filterable: false,
			renderCell: (res: GridRenderCellParams) =>
				res.api.getAllRowIds().indexOf(res.id) + 1,
		}

		const actionCol: GridColDef = {
			field: 'actions',
			type: 'actions',
			headerName: '액션',
			width: 90,
			headerAlign: 'center',
			align: 'center',
			cellClassName: 'actions',
			getActions: (params: GridRowParams) => {
				return [
					<ActionButton
						style={{ margin: '0 0' }}
						key="rowActionDelete"
						iconName="delete-forever"
						onClick={onDeleteClick(params)}
						tooltip="삭제"
						title="삭제"
						iconTextColor={pink['A400']}
					/>,
				]
			},
		}

		const orderedColumns = (() => {
			const map = new Map(props.columns.columns.map(c => [c.field, c]))
			const ordered: GridColDef[] = []
			columnOrder.forEach(f => {
				const c = map.get(f)
				if (c) ordered.push(c)
			})
			// columnOrder에 없는 컬럼은 끝에 추가 (방어적)
			props.columns.columns.forEach(c => {
				if (!columnOrder.includes(c.field)) ordered.push(c)
			})
			return ordered
		})()

		const tempColumns = orderedColumns.map(aColumn => {
			const field = aColumn.field
			const newColumn =
				aColumn.preProcessEditCellProps !== undefined
					? { ...aColumn, renderEditCell: renderEdit }
					: aColumn.type === 'boolean'
					? { ...aColumn, renderEditCell: rederCheckboxEdit }
					: { ...aColumn }

			// 수정 모드 OFF일 땐 모든 셀 편집 비활성 (원래 editable=true 컬럼만 모드 ON 시 편집 가능)
			// alwaysEditable 플래그가 있으면 수정 모드와 무관하게 편집 허용
			if (!editModeOn && !(aColumn as any).alwaysEditable) {
				newColumn['editable'] = false
			}

			if (aColumn.type === 'date' || aColumn.type === 'dateTime') {
				newColumn['filterOperators'] = [
					...(aColumn.filterOperators ?? []),
					DATE_BETWEEN_OPERATOR,
				]
			}

			if (props.renderCell && props.renderCell[field] !== undefined) {
				newColumn['renderCell'] = props.renderCell[field]
			}

			if (props.renderEditCell && props.renderEditCell[field] !== undefined) {
				newColumn['renderEditCell'] = props.renderEditCell[field]
			}

			// 기본 sort 비활성 — sort는 자체 아이콘 버튼으로만 트리거
			newColumn['sortable'] = false

			// 헤더 = [컬럼명] [sort 아이콘 버튼] (메뉴 버튼은 default로 우측에 그려짐)
			if (newColumn.renderHeader === undefined) {
				newColumn['renderHeader'] = (params: any) => {
					const dir = sortModel.find(s => s.field === params.field)?.sort ?? null
					const iconName =
						dir === 'asc' ? 'sort-ascending' : dir === 'desc' ? 'sort-descending' : 'sort'
					return (
						<View
							css={{
								display: 'flex',
								flexDirection: 'row',
								alignItems: 'center',
								width: '100%',
								gap: '0.25rem',
							}}
						>
							<span
								css={{
									flex: 1,
									minWidth: 0,
									overflow: 'hidden',
									textOverflow: 'ellipsis',
									whiteSpace: 'nowrap',
									fontWeight: 500,
								}}
							>
								{params.colDef.headerName ?? params.field}
							</span>
							<button
								type="button"
								className={`gridSortBtn${dir ? ' active' : ''}`}
								title={
									dir === 'asc'
										? '내림차순 정렬'
										: dir === 'desc'
										? '정렬 해제'
										: '오름차순 정렬'
								}
								onMouseDown={e => e.stopPropagation()}
								onClick={e => {
									e.stopPropagation()
									toggleSort(params.field)
								}}
								css={{
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									width: 22,
									height: 22,
									border: 'none',
									background: 'transparent',
									cursor: 'pointer',
									padding: 0,
									borderRadius: 3,
									color: dir ? '#1976d2' : grey[700],
									'&:hover': { backgroundColor: grey[100] },
								}}
							>
								<span
									className={`mdi mdi-${iconName}`}
									css={{ fontSize: 16, lineHeight: 1 }}
								/>
							</button>
						</View>
					)
				}
			}

			return newColumn
		})

		const res: GridColDef[] = isEditable
			? [actionCol, indexingCol, ...tempColumns]
			: [indexingCol, ...tempColumns]

		return res
	}, [
		props.columns,
		columnOrder,
		rows,
		props.rows,
		reorderColumn,
		startHeaderDrag,
		sortModel,
		toggleSort,
		editModeOn,
	])

	const onDeleteClick = (props: GridRowParams) => () => {
		const { row } = props

		setDeleteRow(row)
		setShowModal(true)
	}

	const onModalDelete = React.useCallback(() => {
		const newRows = rows.filter(aRow => {
			return aRow.id !== deleteRow.id
		})
		setRows(newRows)

		if (typeof props.onAction === 'function') {
			props.onAction('D', deleteRow)
		}
	}, [deleteRow])

	const processRowUpdate = (newRow: GridRowModel, oldRow: GridRowModel) => {
		const isNew = newRow.isNew !== undefined

		// cell 모드에서는 사용자가 별도로 isEdited 플래그를 세팅하지 않으므로 값 비교로 변경 감지
		let isChanged = false
		const allKeys = new Set<string>([
			...Object.keys(newRow),
			...Object.keys(oldRow ?? {}),
		])
		for (const key of allKeys) {
			if (key === 'isNew' || key === 'isEdited') continue
			if (newRow[key] !== (oldRow ?? {})[key]) {
				isChanged = true
				break
			}
		}

		if (isNew && typeof props.onAction === 'function') {
			props.onAction('I', newRow)
		} else if (isChanged && typeof props.onAction === 'function') {
			props.onAction('U', newRow)
		}

		const cleaned: GridRowModel = { ...newRow }
		delete cleaned.isNew
		delete cleaned.isEdited

		setRows(prev => prev.map(aRow => (aRow.id === newRow.id ? cleaned : aRow)))

		return cleaned
	}

	const onAddClick = () => {
		const id = randomId()
		setRows(oldRows => [...oldRows, { id: id, ...props.columns?.initRow, isNew: true }])
		const fieldToFocus = props.columns?.fieldToFocus
		if (fieldToFocus) {
			// 새 row가 그리드에 반영된 후 cell 편집 모드 진입
			setTimeout(() => {
				try {
					apiRef.current?.startCellEditMode({ id, field: fieldToFocus })
				} catch {
					/* noop */
				}
			}, 0)
		}
	}

	return (
		<View css={style}>
			{isLoading && (
				<View className="gridLoadingCircleView">
					<CircularProgress />
				</View>
			)}
			<View className={`gridContainer${editModeOn ? ' gridContainerEdit' : ''}`}>
				<View className="gridLeft">
					<DataGrid
						{...props}
						rowHeight={35}
						columnHeaderHeight={35}
						apiRef={apiRef}
						rows={rows}
						columns={newColumns}
						editMode="cell"
						sortingOrder={['asc', 'desc', null]}
						localeText={koKR.components.MuiDataGrid.defaultProps.localeText}
						paginationModel={paginationModel}
						onPaginationModelChange={handlePaginationModelChange}
						pageSizeOptions={PAGE_SIZE_OPTIONS}
						slots={{
							toolbar: GridToolBar,
							pagination: CustomPagination,
						}}
						slotProps={{
							toolbar: {
								setRows,
								isLoading,
								apiRef,
								onAddClick,
								title: props.title,
								onExcelUpload: props.onExcelUpload,
								onExcelUploadClick: props.onExcelUploadClick,
								leftToolbarSlot: props.leftToolbarSlot,
								editModeOn,
								onEditModeToggle: setEditModeOn,
								hasActionColumn: isEditable,
							},
							loadingOverlay: {
								variant: 'skeleton',
								noRowsVariant: 'skeleton',
							},
						}}
						onRowSelectionModelChange={newRowSelectionModel => {
							if (typeof props.onRowSelectionModelChange === 'function') {
								props.onRowSelectionModelChange(newRowSelectionModel)
							}
						}}
						disableRowSelectionOnClick={checkboxSelection}
						checkboxSelection={checkboxSelection}
						rowSelectionModel={props.rowSelectionModel}
						indeterminateCheckboxAction="select"
						isRowSelectable={
							props.isRowSelectable ?? ((params: GridRowParams) => params.row.status)
						}
						onProcessRowUpdateError={() => {}}
						processRowUpdate={processRowUpdate}
						loading={isLoading}
						disableColumnSorting={isLoading}
						disableColumnMenu={isLoading}
						sx={{
							border: 'none',
							borderRadius: 0,
							fontSize: '0.8125rem',
							color: '#334155',
							// 헤더 — 차분한 slate 톤
							'& .MuiDataGrid-columnHeaders': {
								borderBottom: '1px solid #e2e8f0',
							},
							'& .MuiDataGrid-columnHeader': {
								cursor: 'grab',
								backgroundColor: '#f8fafc',
								transition: 'background-color 120ms ease',
							},
							'& .MuiDataGrid-columnHeader:hover': {
								backgroundColor: '#f1f5f9',
							},
							'& .MuiDataGrid-columnHeader:active': { cursor: 'grabbing' },
							'& .MuiDataGrid-columnHeaderTitle': {
								fontWeight: 600,
								color: '#334155',
								letterSpacing: '0.01em',
							},
							'& .MuiDataGrid-columnHeader .gridSortBtn, & .MuiDataGrid-columnHeader .MuiIconButton-root':
								{
									cursor: 'pointer',
								},
							'& .MuiDataGrid-columnSeparator': {
								cursor: 'col-resize',
								color: '#e2e8f0',
							},
							// title 영역이 cell 폭을 가능한 한 채워서 sort 버튼이 메뉴 옆까지 밀림
							'& .MuiDataGrid-columnHeaderTitleContainerContent': { flex: 1 },
							// MUI 기본 sort 아이콘은 숨김 (우리 자체 버튼만 사용)
							'& .MuiDataGrid-sortIcon': { display: 'none' },
							'& .MuiDataGrid-iconButtonContainer': {
								// sort indicator만 빠지면 컨테이너가 빈 공간만 차지하므로 width 0
								width: 0,
								visibility: 'hidden',
							},
							// sort 버튼은 평소엔 숨기고, 컬럼 hover 또는 sort 활성 시에만 노출
							'& .gridSortBtn': { visibility: 'hidden' },
							'& .gridSortBtn.active': { visibility: 'visible' },
							'& .MuiDataGrid-columnHeader:hover .gridSortBtn': {
								visibility: 'visible',
							},
							'& .MuiDataGrid-columnHeader.colDropHover': {
								boxShadow: 'inset 3px 0 0 #1976d2',
								backgroundColor: 'rgba(25, 118, 210, 0.08)',
							},
							// 행 — 미세한 zebra + 부드러운 hover
							'& .MuiDataGrid-row': {
								transition: 'background-color 120ms ease',
							},
							'& .MuiDataGrid-row:nth-of-type(even)': {
								backgroundColor: 'rgba(248, 250, 252, 0.6)',
							},
							'& .MuiDataGrid-row:hover': {
								backgroundColor: 'rgba(59, 130, 246, 0.06)',
							},
							'& .MuiDataGrid-row.Mui-selected': {
								backgroundColor: 'rgba(59, 130, 246, 0.10)',
							},
							'& .MuiDataGrid-row.Mui-selected:hover': {
								backgroundColor: 'rgba(59, 130, 246, 0.14)',
							},
							// 삭제/환불 등 비활성 상태 row — getRowClassName 으로 지정
							'& .MuiDataGrid-row.row-inactive': {
								backgroundColor: 'rgba(254, 226, 226, 0.7)',
							},
							'& .MuiDataGrid-row.row-inactive:hover': {
								backgroundColor: 'rgba(254, 202, 202, 0.85)',
							},
							'& .MuiDataGrid-row.row-inactive.Mui-selected': {
								backgroundColor: 'rgba(252, 165, 165, 0.55)',
							},
							// 셀 — 가벼운 구분선 + focus outline 제거
							'& .MuiDataGrid-cell': {
								borderBottom: '1px solid #f1f5f9',
								outline: 'none !important',
							},
							'& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
								outline: 'none !important',
							},
							// 액션 컬럼 셀 padding 정돈
							'& .MuiDataGrid-cell.actions': {
								padding: 0,
							},
							// 페이지네이션 / 푸터
							'& .MuiDataGrid-footerContainer': {
								height: '42px',
								minHeight: '42px',
								borderTop: '1px solid #e2e8f0',
								backgroundColor: '#fafbfc',

								'.MuiTablePagination-root': {
									height: '100%',
									minHeight: '42px',
									color: '#475569',
									fontSize: '0.8125rem',

									'.MuiTablePagination-toolbar': {
										height: '42px',
										minHeight: '42px',
									},
								},
							},
							'& .MuiPagination-root .MuiPaginationItem-root': {
								fontSize: '0.8125rem',
								minWidth: 28,
								height: 28,
							},
							// 체크박스
							'& .MuiCheckbox-root': {
								color: '#cbd5e1',
							},
							'& .MuiCheckbox-root.Mui-checked': {
								color: '#3b82f6',
							},
						}}
					/>
				</View>
				{sidePanelEnabled && (
					<GridSidePanel
						apiRef={apiRef}
						columns={newColumns}
						openTab={sidePanelTab}
						onChangeTab={setSidePanelTab}
						onReorder={reorderColumn}
					/>
				)}
			</View>

			<Modal
				target="newGroup"
				title="삭제"
				show={showModal}
				onSubmit={onModalDelete}
				onCloseModal={() => setShowModal(false)}
				closeStatus="inherit"
				closeTitle="닫기"
				submitTitle="삭제"
				submitStatus="error"
				submitIcon="trash-can-outline"
				submitIconColor={pink['A400']}
			>
				<View css={{ width: '30rem' }}>
					<Text category="p2">이 데이터를 삭제하면 복구할 수 없습니다.</Text>
					<Text category="p2">그래도 삭제하시겠습니까?</Text>
				</View>
			</Modal>
		</View>
	)
}

const style = css({
	width: '100%',
	height: '100%',
	position: 'relative',

	'.gridContainer': {
		display: 'flex',
		flexDirection: 'row',
		width: '100%',
		height: '100%',
		minHeight: 0,
		overflow: 'hidden',
		boxSizing: 'border-box',
		backgroundColor: '#fff',
		border: '1px solid #e2e8f0',
		borderRadius: '8px',
		boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04), 0 1px 3px rgba(15, 23, 42, 0.06)',
		transition: 'border-color 160ms ease, box-shadow 160ms ease',
	},

	// 수정 모드 — 컨테이너 외곽에 amber 톤 강조
	'.gridContainerEdit': {
		borderColor: 'rgba(245, 158, 11, 0.55)',
		boxShadow: '0 0 0 3px rgba(245, 158, 11, 0.10), 0 1px 3px rgba(15, 23, 42, 0.06)',
	},

	// 수정 모드: 편집 가능 셀 hover에 시각 신호 (cursor + amber tint)
	'.gridContainerEdit .MuiDataGrid-cell--editable': {
		cursor: 'cell',
	},
	'.gridContainerEdit .MuiDataGrid-cell--editable:hover': {
		backgroundColor: 'rgba(245, 158, 11, 0.08) !important',
		boxShadow: 'inset 0 0 0 1px rgba(245, 158, 11, 0.45)',
	},
	'.gridContainerEdit .MuiDataGrid-cell--editing': {
		backgroundColor: 'rgba(245, 158, 11, 0.05) !important',
		boxShadow: 'inset 0 0 0 1.5px #f59e0b',
	},

	'.gridLeft': {
		flex: '1 1 0',
		minWidth: 0,
		width: 0,
		display: 'flex',
		flexDirection: 'column',
		overflow: 'hidden',
	},

	'.gridLoadingCircleView': {
		position: 'absolute',
		width: '40px',
		height: '40px',
		zIndex: 10,
		top: 0,
		bottom: 0,
		left: 0,
		right: 0,
		margin: 'auto',
	},
})
